import { ChangeDetectionStrategy, Component, DOCUMENT, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { enterMotion, leaveMotion } from '@ricoh-paper/angular';

import { CallDockState, type IncomingView } from '../data/call-dock.state';
import { pictureInPictureSupported } from '../data/call-pip';
import { CallDockComponent } from './call-dock.component';
import { CallIncomingCardComponent } from './call-incoming-card.component';
import { CallStageOverlayComponent } from './call-stage-overlay.component';
import { FLOW_CALL_DOCK_LIFT, FlowSurfaceService } from '../../../core/services/flow-surface.service';

/** At most this many cards at once; a third caller waits behind the first two. */
const MAX_CARDS = 2;

/**
 * Bars that also sit at the bottom of the page: Flow's floating Ask, the map's time scrubber, Home's Ask dock and
 * the Ask companion. Where one is under the dock, the dock rises above it instead of covering it.
 */
const BOTTOM_BARS = ['[data-testid="flow-floating-ask"]', 'im-time-scrubber', '.ask-dock', '[data-testid="ask-companion-dock"]'];
const DOCK_HALF_WIDTH = 240;
const GAP = 8;

/** How far up from the window's bottom the dock must sit to clear the bars under it; 0 when nothing is there. */
export function dockLift(boxes: readonly { left: number; right: number; top: number; bottom: number }[], width: number, height: number): number {
  const left = width / 2 - DOCK_HALF_WIDTH;
  const right = width / 2 + DOCK_HALF_WIDTH;
  let lift = 0;
  for (const b of boxes) {
    const underDock = b.right > left && b.left < right;
    const atBottom = b.bottom > height - 200 && b.top < height;
    if (underDock && atBottom) lift = Math.max(lift, Math.round(height - b.top + GAP));
  }
  return lift;
}

/**
 * im-call-dock-host — where the call surfaces live (beads indoor_mapping_poc-0sm8.3–.5).
 *
 * Mounted ONCE by the app root (`app.ts`), outside every route, so a call's dock survives navigation, the shell
 * coming and going, and the bare routes. It shows:
 *   - the incoming-call cards, bottom-centre, above where the dock sits;
 *   - the dock for an active direct call (`im-call-dock`);
 *   - the call stage over the page when the dock's Open stage is pressed (loaded on first use).
 * It draws nothing at all while there is no call and nobody calling, and nothing for a hosted meeting, whose call
 * lives on its own page.
 *
 * `aboveNav`: the app shell's floating bottom navigation is on screen (phones), so the dock sits above it
 * (`--bottom-nav-height`, which is 0 from 820px up).
 */
@Component({
  selector: 'im-call-dock-host',
  standalone: true,
  imports: [TranslocoPipe, CallDockComponent, CallIncomingCardComponent, CallStageOverlayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture', '[class.above-nav]': 'aboveNav()', '[style.--call-dock-lift.px]': 'liftNow()' },
  template: `
    @if (showFloating()) {
      <div class="float" data-testid="emp.calls.dock-host">
        @if (cards().length > 0) {
          <div class="cards" aria-live="assertive">
            @for (card of cards(); track card.kind + card.callId) {
              <im-call-incoming-card
                [view]="card"
                [fallbackName]="'flow_call.dock.colleague' | transloco"
                (accept)="accept(card)"
                (decline)="decline(card)"
                (silence)="state.silence(card)"
                (animate.enter)="enter($event)"
                (animate.leave)="leave($event)"
              />
            }
          </div>
        }
        @if (state.dock(); as dock) {
          <im-call-dock
            [view]="dock"
            [pipSupported]="pipSupported"
            [pipActive]="state.pipActive()"
            (toggleMute)="state.toggleMute()"
            (toggleCamera)="state.toggleCamera()"
            (togglePip)="state.togglePip()"
            (openStage)="state.openStage()"
            (hangUp)="state.hangUp()"
            (resumeAudio)="state.resumeAudio()"
            (dismiss)="state.dismissFailure()"
            (animate.enter)="enter($event)"
            (animate.leave)="leave($event)"
          />
        }
      </div>
    }
    @if (stageShown()) {
      @defer (on immediate) {
        <im-call-stage-overlay (minimise)="state.closeStage()" />
      }
    }
  `,
  styles: `
    :host { display: contents; }
    .float {
      position: fixed;
      left: 50%;
      bottom: max(calc(16px + env(safe-area-inset-bottom, 0px)), var(--call-dock-lift, 0px));
      z-index: 60; /* Z_INDEX.FLOATING: over page chrome and map panels, under popovers, ⌘K and dialogs */
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      width: max-content;
      max-width: calc(100vw - 32px);
      transform: translateX(-50%);
      pointer-events: none;
      transition: bottom var(--motion-fast, 160ms) var(--ease, ease);
    }
    @media (prefers-reduced-motion: reduce) {
      .float { transition: none; }
    }
    :host(.above-nav) .float {
      bottom: max(calc(16px + var(--bottom-nav-height, 0px) + env(safe-area-inset-bottom, 0px)), var(--call-dock-lift, 0px));
    }
    .float > * { pointer-events: auto; }
    .cards { display: flex; flex-direction: column; align-items: center; gap: 8px; pointer-events: auto; }
  `,
})
export class CallDockHostComponent {
  protected readonly state = inject(CallDockState);

  /** The shell's bottom navigation is showing: sit above it. */
  readonly aboveNav = input(false);

  protected readonly pipSupported = pictureInPictureSupported();
  protected readonly cards = computed<readonly IncomingView[]>(() => this.state.incoming().slice(0, MAX_CARDS));
  protected readonly stageShown = computed(() => this.state.stageOpen() && !!this.state.dock());
  /** The stage covers the page; the dock and cards wait under it. */
  protected readonly showFloating = computed(() => !this.stageShown() && (!!this.state.dock() || this.cards().length > 0));

  /** Clearance over a bottom bar under the dock (Flow's Ask, the map's scrubber), measured once a second. */
  protected readonly lift = signal(0);
  /** On a Flow surface the dock sits over the Ask band (bottom 72 + 12), never on the Ask bar, whether or not the bar has been measured yet. */
  private readonly flowSurface = inject(FlowSurfaceService);
  protected readonly liftNow = computed(() => Math.max(this.lift(), this.flowSurface.surface() ? FLOW_CALL_DOCK_LIFT : 0));

  protected readonly enter = enterMotion();

  constructor() {
    const doc = inject(DOCUMENT);
    let liftNow = 0;
    const timer = setInterval(() => {
      if (!this.showFloating()) return;
      const view = doc.defaultView;
      if (!view) return;
      const boxes = BOTTOM_BARS.map((sel) => doc.querySelector(sel))
        .filter((el): el is HTMLElement => el instanceof HTMLElement)
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r.width > 0 && r.height > 0);
      const next = dockLift(boxes, view.innerWidth, view.innerHeight);
      if (next !== liftNow) {
        liftNow = next;
        this.lift.set(next);
      }
    }, 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }
  protected readonly leave = leaveMotion();

  protected accept(card: IncomingView): void {
    void this.state.accept(card);
  }

  protected decline(card: IncomingView): void {
    void this.state.decline(card);
  }
}
